import { ReactNode } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from '@heroui/react';
import CustomPagination from './common/ui/CustomPagination';
import { useResponsive } from '../../hook/useResponsive';
import CustomAlert from './CustomAlert';

export interface TableColumn<T> {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T, index?: number) => ReactNode;
}

interface CommonTableModalProps<T> {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onClose: () => void;
  title: string;
  columns: TableColumn<T>[];
  data: T[];
  isLoading?: boolean;
  caption?: string;
  footerActions?: (onClose: () => void) => ReactNode;
  currentPage?: number;
  totalPages?: number;
  totalData?: number;
  onPageChange?: (page: number) => void;
  alertTitle?: string;
}

function CommonTableModal<T extends { id: string | number }>({
  isOpen,
  onOpenChange,
  title,
  columns,
  data = [],
  isLoading = false,
  caption,
  footerActions,
  currentPage,
  totalPages,
  totalData,
  onPageChange,
  alertTitle = 'Provider Account Cannot Be Deactivated',
}: CommonTableModalProps<T>) {
  const { isMobile } = useResponsive();
  console.log('CommonTableModal Rendered with data:', {
    totalPages,
    totalData,
    currentPage,
    dataLength: data.length,
    caption,
    columns,
  });

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size={isMobile ? 'full' : '5xl'}
      scrollBehavior="inside"
      backdrop="opaque"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1 text-xl font-bold tracking-tight text-foreground">
              {title}
            </ModalHeader>

            <ModalBody className="gap-4">
              <div className="">
                <CustomAlert
                  title={alertTitle}
                  description="Your account cannot be deactivated while you have active bookings. Please complete, cancel, or resolve all active bookings before attempting to deactivate your account."
                  color="warning"
                />
              </div>

              {/* Responsive Container Wrapper */}
              <div className="relative w-full overflow-x-auto rounded-xl border border-default-200 shadow-sm bg-content1 scrollbar-thin">
                {/* Backdrop Blur Loading Overlay */}
                {isLoading && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/50 backdrop-blur-xs rounded-sm transition-all">
                    <Spinner size="sm" color="primary" label="Loading..." />
                  </div>
                )}

                <table className="w-full text-sm border-collapse min-w-[500px]">
                  {/* Head Section */}
                  <thead>
                    {/* Row 1: The Main Columns */}
                    <tr className="bg-default-100 border-b border-default-200">
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          style={{ width: col.width }}
                          className={[
                            'px-5 py-2 text-xs font-semibold text-default-500 uppercase tracking-wider select-none',
                            col.align === 'center'
                              ? 'text-center'
                              : col.align === 'right'
                                ? 'text-right'
                                : 'text-left',
                          ].join(' ')}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  {/* Body Section */}
                  <tbody className="divide-y divide-default-100 font-normal text-default-700">
                    {data.length > 0 ? (
                      data.map((row, index) => (
                        <tr
                          key={row.id}
                          className="hover:bg-primary-50/80 transition-colors duration-150"
                        >
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className={[
                                'px-5 py-3.5 whitespace-nowrap',
                                col.align === 'center'
                                  ? 'text-center'
                                  : col.align === 'right'
                                    ? 'text-right'
                                    : 'text-left',
                              ].join(' ')}
                            >
                              {col.render
                                ? col.render(
                                    row[col.key as keyof T],
                                    row,
                                    index
                                  )
                                : ((row[col.key as keyof T] ??
                                    '—') as ReactNode)}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-5 py-12 text-center text-default-400 font-medium bg-default-50/30"
                        >
                          {isLoading ? '' : ' No records found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className='className="px-1 py-1 '>
                {totalData !== undefined && totalData > 0 && (
                  <p className=" text-xs font-medium text-default-400 text-left">
                    Showing{' '}
                    <span className="text-default-600 font-semibold">
                      {totalData}
                    </span>{' '}
                    total records
                  </p>
                )}

                {totalPages &&
                  totalPages > 1 &&
                  currentPage &&
                  onPageChange && (
                    <div className="flex justify-end ">
                      <CustomPagination
                        page={currentPage}
                        initialPage={1}
                        total={totalPages}
                        size="sm"
                        onChange={onPageChange}
                      />
                    </div>
                  )}
              </div>

              {/* Dynamic Optional Pagination */}
            </ModalBody>

            <ModalFooter className="border-t border-default-100">
              <Button color="danger" variant="flat" onPress={onClose}>
                Close
              </Button>
              {/* Execute the footer button render prop if supplied */}
              {footerActions && footerActions(onClose)}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

export default CommonTableModal;
